import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import Maintenance from "@/pages/Maintenance";
import { Wrench, X } from "lucide-react";

// Paths that stay reachable during maintenance so admins can still sign in.
const ALLOWED_PATHS = ["/login", "/sso/callback"];

// Wraps the whole app. When maintenance mode is ON, every non-admin visitor is
// shown the full-screen Maintenance page (except on /login and /sso/callback).
// Founders/admins bypass and see the live site with a thin banner on top.
export default function MaintenanceGate({ children }) {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  const [cfg, setCfg] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  const loadCfg = async () => {
    try {
      const { data } = await api.get("/config/maintenance");
      setCfg(data);
    } catch {
      setCfg({ maintenance_mode: false, message: "" });
    }
  };

  useEffect(() => {
    loadCfg();
    const id = setInterval(loadCfg, 30000); // re-check every 30s
    // Admin toggling maintenance from Site Settings fires this so the banner
    // appears/disappears instantly instead of waiting for the next poll.
    const onChanged = () => loadCfg();
    window.addEventListener("maintenance-settings-changed", onChanged);
    return () => {
      clearInterval(id);
      window.removeEventListener("maintenance-settings-changed", onChanged);
    };
  }, []);

  const isAdmin = !!user && (user.role === "founder" || user.role === "admin");
  const showBanner = !!(cfg && cfg.maintenance_mode && isAdmin && !dismissed);

  // Offset the fixed navbar + main content when the admin banner is showing so
  // it doesn't overlay the header (padding can't move a position:fixed navbar).
  useEffect(() => {
    const cls = "has-maint-banner";
    if (showBanner) document.body.classList.add(cls);
    else document.body.classList.remove(cls);
    return () => document.body.classList.remove(cls);
  }, [showBanner]);

  // Wait for both the maintenance config and the auth state to resolve so an
  // admin isn't briefly shown the maintenance page on first paint.
  if (cfg === null || authLoading) {
    return (
      <div className="min-h-screen bg-[var(--ff-bg,#0a0a0a)] flex items-center justify-center">
        <div className="text-[var(--ff-text-dim,#9a9a9a)] font-mono-ui text-xs uppercase tracking-[0.25em] animate-pulse">
          Loading…
        </div>
      </div>
    );
  }

  const onAllowedPath = ALLOWED_PATHS.includes(location.pathname);

  if (cfg.maintenance_mode && !isAdmin && !onAllowedPath) {
    return <Maintenance message={cfg.message} />;
  }

  return (
    <>
      {showBanner && (
        <div
          className="fixed top-0 inset-x-0 z-[60] h-9 bg-[var(--ff-red)] text-white flex items-center justify-center gap-3 px-4 text-[0.7rem] font-mono-ui uppercase tracking-[0.18em]"
          data-testid="maintenance-admin-banner"
        >
          <Wrench size={13} />
          Maintenance mode is ON — only founders &amp; admins can access the site.
          <button
            onClick={() => setDismissed(true)}
            className="ml-2 opacity-80 hover:opacity-100"
            data-testid="maintenance-banner-dismiss"
            aria-label="Dismiss"
          >
            <X size={13} />
          </button>
        </div>
      )}
      {children}
    </>
  );
}
